import * as THREE from 'three';
import { blockingObject, type SiteObject } from './SiteObject';
import { toWorldPoint } from './SiteFootprint';
import {
  buildSiteObjectGeometry,
  groundSamplePoints,
  siteObjectHeight,
} from './SiteObjectGeometry';
import { siteModels, type SiteObjectModelRegistry } from './SiteObjectModelSource';

/**
 * Owns the canonical set of {@link SiteObject}s and keeps matching Three.js views in sync, exactly
 * as {@link FloraWorld} does for plants: records are the source of truth, views are rebuilt from
 * them, and nothing renderer-owned ever becomes canonical.
 *
 * The one piece of real work here is sitting an object on uneven ground. A footprint is a polygon
 * but terrain is not flat, so the world samples the ground under each of its vertices and centre,
 * places the object at the **high** point, and passes the drop to the geometry as a skirt. A
 * building is then cut into the slope rather than floating over its downhill corner.
 */

/** Samples terrain height at a world (x, z). Supplied by the app; absent means a flat site. */
export type GroundSampler = (x: number, z: number) => number;

export class SiteObjectWorld {
  private readonly objects = new Map<string, SiteObject>();
  private readonly views = new Map<string, THREE.Object3D>();
  private readonly materials: THREE.Material[] = [];
  private ground: GroundSampler | null = null;
  private readonly unsubscribeModels: () => void;

  constructor(
    private readonly root: THREE.Object3D,
    private readonly models: SiteObjectModelRegistry = siteModels,
  ) {
    // Assets arrive after the scene does. Without this, a project opened while its props were still
    // downloading would render as placeholder massing and stay that way, since nothing else in the
    // application has a reason to touch those objects again.
    this.unsubscribeModels = this.models.onChange((modelId) => this.rebuildModel(modelId));
  }

  /** Rebuild every view using this model id — called when an asset finishes loading. */
  private rebuildModel(modelId: string): void {
    for (const object of this.objects.values()) {
      if (object.modelId !== modelId) continue;
      this.disposeView(object.id);
      this.buildView(object);
    }
  }

  get count(): number {
    return this.objects.size;
  }

  /** Point the world at the current terrain. Rebuilds every view, since ground heights moved. */
  setGroundSampler(sampler: GroundSampler | null): void {
    this.ground = sampler;
    for (const object of this.objects.values()) {
      this.disposeView(object.id);
      this.buildView(object);
    }
  }

  add(object: SiteObject): void {
    if (this.objects.has(object.id)) {
      throw new Error(`Site object already exists: ${object.id}`);
    }
    this.objects.set(object.id, object);
    this.buildView(object);
  }

  remove(id: string): SiteObject | undefined {
    const object = this.objects.get(id);
    if (!object) return undefined;
    this.objects.delete(id);
    this.disposeView(id);
    return object;
  }

  update(next: SiteObject): void {
    if (!this.objects.has(next.id)) {
      throw new Error(`Cannot update unknown site object: ${next.id}`);
    }
    this.objects.set(next.id, next);
    this.disposeView(next.id);
    this.buildView(next);
  }

  has(id: string): boolean {
    return this.objects.has(id);
  }

  get(id: string): SiteObject | undefined {
    return this.objects.get(id);
  }

  all(): SiteObject[] {
    return [...this.objects.values()];
  }

  view(id: string): THREE.Object3D | undefined {
    return this.views.get(id);
  }

  /** Replace the whole set — the "open project" path. */
  load(objects: readonly SiteObject[]): void {
    this.clear();
    for (const object of objects) {
      if (this.objects.has(object.id)) continue;
      this.add(object);
    }
  }

  clear(): void {
    for (const id of [...this.objects.keys()]) {
      this.objects.delete(id);
      this.disposeView(id);
    }
  }

  /**
   * The object blocking this point, if any. Placement asks before dropping a plant, so a hedge can
   * never be planted through a wall.
   */
  blocking(x: number, z: number, extraMargin = 0): SiteObject | undefined {
    return blockingObject(this.all(), x, z, extraMargin);
  }

  dispose(): void {
    this.unsubscribeModels();
    for (const id of [...this.views.keys()]) this.disposeView(id);
    for (const material of this.materials) material.dispose();
    this.materials.length = 0;
  }

  private buildView(object: SiteObject): void {
    const { seat, skirt } = this.groundUnder(object);

    const group = new THREE.Group();
    group.name = `site:${object.id}`;
    group.userData.siteObjectId = object.id;

    // Authored asset first, generated geometry second — the same precedence `FloraFactory` gives its
    // model registry. A provider that declines (no asset yet, or none for this request) falls through
    // rather than leaving a hole, so the object is always *something*.
    const model = this.modelView(object, skirt);
    if (model) {
      group.add(model);
      // Model-backed views own their materials but share the prototype's geometry, so they have to be
      // torn down differently. Recorded here rather than inferred later, because getting it wrong
      // disposes buffers still in use by every other instance of the same asset.
      group.userData.modelBacked = true;
    } else {
      const parts = buildSiteObjectGeometry(object, { groundSkirtMeters: skirt });
      if (parts.body) group.add(this.mesh(parts.body, object.wallColor, 'body'));
      if (parts.roof) group.add(this.mesh(parts.roof, object.roofColor, 'roof'));
    }

    group.position.set(object.position.x, seat + object.position.y, object.position.z);
    group.rotation.y = object.rotationY;
    group.visible = !object.hidden;

    this.views.set(object.id, group);
    this.root.add(group);
  }

  /** Ask the registry for an authored mesh, or `null` to fall through to generated geometry. */
  private modelView(object: SiteObject, skirt: number): THREE.Object3D | null {
    if (!object.modelId) return null;
    const provider = this.models.resolve(object.modelId);
    if (!provider) return null;
    return provider.build({ object, groundSkirtMeters: skirt });
  }

  private mesh(geometry: THREE.BufferGeometry, color: string, name: string): THREE.Mesh {
    const material = new THREE.MeshStandardMaterial({
      color: new THREE.Color(color),
      roughness: name === 'roof' ? 0.85 : 0.72,
      metalness: 0,
      flatShading: true,
    });
    this.materials.push(material);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = name;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  /**
   * Ground under a footprint: the height to sit at, and how far the lowest sampled point falls
   * below it.
   *
   * The samples are the footprint's own vertices plus each ring's centre (`groundSamplePoints`), so
   * an L-shaped house is measured at the corners it actually has. Sampling a rectangle derived from
   * a polygon would read the ground at corners the building does not occupy, and on a slope that is
   * the difference between a skirt that closes the gap and one that leaves daylight under a wall.
   */
  private groundUnder(object: SiteObject): { seat: number; skirt: number } {
    const sampler = this.ground;
    if (!sampler) return { seat: 0, skirt: 0 };

    let high = -Infinity;
    let low = Infinity;
    let total = 0;
    let count = 0;
    for (const local of groundSamplePoints(object)) {
      const world = toWorldPoint(local, object.position, object.rotationY);
      const height = sampler(world.x, world.z);
      high = Math.max(high, height);
      low = Math.min(low, height);
      total += height;
      count += 1;
    }
    if (!Number.isFinite(high) || count === 0) return { seat: 0, skirt: 0 };

    // Buildings and paving sit at the *high* point and are cut into the slope beneath: a foundation
    // is level, and daylight under a downhill wall is the failure to avoid.
    //
    // A prop is the opposite case. It is a loose object resting on the ground, not excavated into it,
    // so it sits at the mean height its footprint covers — which is what a bench on a mild slope
    // actually does. Seating a bench at its high corner would stand it on its downhill legs.
    if (object.kind !== 'prop') {
      return { seat: high, skirt: Math.max(0, high - low) };
    }

    const mean = total / count;
    return { seat: mean, skirt: Math.max(0, mean - low) };
  }

  /**
   * Tear down a view, freeing exactly what that view owns.
   *
   * The two kinds of view own opposite halves. Generated geometry is built per object and disposed
   * here, while its materials live in the shared `materials` list and outlive it. A model-backed view
   * is the mirror image: it *shares* the prototype's buffers with every other instance of that asset,
   * so disposing them here would empty the screen of every other copy, but it clones its materials
   * per instance, so those would leak if left.
   */
  private disposeView(id: string): void {
    const view = this.views.get(id);
    if (!view) return;
    this.root.remove(view);

    const modelBacked = view.userData.modelBacked === true;
    view.traverse((node) => {
      const mesh = node as Partial<THREE.Mesh>;
      if (mesh.isMesh !== true) return;
      if (modelBacked) {
        const material = mesh.material;
        if (Array.isArray(material)) for (const entry of material) entry.dispose();
        else material?.dispose();
      } else {
        mesh.geometry?.dispose();
      }
    });
    this.views.delete(id);
  }

  /** Highest point any site object reaches, for scene bounds. */
  maxHeight(): number {
    let max = 0;
    for (const object of this.objects.values()) {
      if (object.hidden) continue;
      max = Math.max(max, siteObjectHeight(object));
    }
    return max;
  }
}
